import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, MenuItem, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { apiFetch } from '../utils/apiFetch';
import { isStoredSessionTokenCurrent } from '../utils/auth';

export const formatNinetyNineFoodCents = (value) => {
  if ((typeof value === 'number' && !Number.isSafeInteger(value))
    || !['string', 'number', 'bigint'].includes(typeof value) || !/^-?\d+$/.test(String(value))) return 'Não informado';
  // Format decimal digits directly: no float rounding and no BigInt syntax,
  // which would prevent the settings bundle from parsing on supported iOS12.
  const digits = String(value).replace(/^-/, '').replace(/^0+(?=\d)/, '').padStart(3, '0');
  const units = digits.slice(0, -2).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${String(value).startsWith('-') && /[1-9]/.test(digits) ? '-' : ''}R$ ${units},${digits.slice(-2)}`;
};
export const validNinetyNineFoodFinancialPeriod = (start, end) => {
  if (![start, end].every((value) => /^\d{4}-\d{2}-\d{2}$/.test(value))) return false;
  const first = Date.parse(`${start}T12:00:00Z`), last = Date.parse(`${end}T12:00:00Z`);
  return Number.isFinite(first) && Number.isFinite(last)
    && new Date(first).toISOString().slice(0, 10) === start && new Date(last).toISOString().slice(0, 10) === end
    && last >= first && last - first < 31 * 86400000;
};
const today = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
const BILL_COLUMNS = [
  ['orderId', 'Pedido'], ['orderIndex', 'Número'], ['orderType', 'Tipo'], ['businessDateTime', 'Data'],
  ['orderAmount', 'Valor do pedido', true], ['commissionAmount', 'Comissão', true],
  ['settlementAmount', 'Valor de repasse', true], ['cashBalance', 'Saldo em dinheiro', true],
  ['expectSettleDate', 'Previsão de repasse'], ['paymentMethod', 'Pagamento'], ['paymentChannel', 'Canal de pagamento'],
];
const SETTLEMENT_COLUMNS = [
  ['weekPaymentId', 'Identificador'], ['withdrawDate', 'Data de desembolso'], ['withdrawAmount', 'Valor', true],
  ['settleStartDate', 'Período inicial'], ['settleEndDate', 'Período final'], ['currency', 'Moeda'], ['cercAmount', 'Valor CERC', true],
];

const NinetyNineFoodFinancialPanel = ({ apiUrl, sessionToken }) => {
  const [kind, setKind] = useState('bills');
  const [start, setStart] = useState(today);
  const [end, setEnd] = useState(today);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const current = useRef({ mounted: true, sessionToken, generation: 0 });
  const controller = useRef(null);
  current.current.sessionToken = sessionToken;
  useEffect(() => {
    const state = current.current;
    state.mounted = true;
    return () => { state.mounted = false; state.generation += 1; controller.current?.abort(); };
  }, [sessionToken]);
  const invalidate = () => { current.current.generation += 1; controller.current?.abort(); setLoading(false); setResult(null); setError(''); };
  const read = async (page = 1) => {
    if (!isStoredSessionTokenCurrent(sessionToken)) return;
    if (!validNinetyNineFoodFinancialPeriod(start, end)) { setError('Escolha um período válido de até 31 dias.'); return; }
    controller.current?.abort(); controller.current = new AbortController();
    const generation = ++current.current.generation;
    const active = () => current.current.mounted && current.current.generation === generation
      && current.current.sessionToken === sessionToken && isStoredSessionTokenCurrent(sessionToken);
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams({ start, end, page: String(page) });
      const response = await apiFetch(`${apiUrl}/api/99food/finance/${kind}?${params}`, { signal: controller.current.signal });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Não foi possível consultar o financeiro da 99Food.');
      if (!active()) return;
      setResult({ ...data, records: Array.isArray(data.records) ? data.records : [], kind, ownerToken: sessionToken });
    } catch (failure) { if (active() && failure.name !== 'AbortError') { setError(failure.message); setResult(null); } }
    finally { if (active()) setLoading(false); }
  };
  const visibleResult = result?.ownerToken === sessionToken ? result : null;
  const columns = (visibleResult?.kind || kind) === 'bills' ? BILL_COLUMNS : SETTLEMENT_COLUMNS;
  return (
    <Card data-testid="99food-financial-panel"><CardContent>
      <Typography variant="h6" gutterBottom>Financeiro oficial 99Food</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Consulta de extratos e repasses da 99Food. Um extrato de pedidos não comprova quitação. A data de desembolso é a informação de repasse reportada pela plataforma; esta consulta não registra recebimentos no caixa.</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} sx={{ mb: 2 }}>
        <TextField select label="Relatório" value={kind} onChange={(event) => { invalidate(); setKind(event.target.value); }}>
          <MenuItem value="bills">Extrato de pedidos</MenuItem><MenuItem value="settlements">Repasses</MenuItem>
        </TextField>
        <TextField type="date" label="Início" value={start} InputLabelProps={{ shrink: true }} onChange={(event) => { invalidate(); setStart(event.target.value); }} />
        <TextField type="date" label="Fim" value={end} InputLabelProps={{ shrink: true }} onChange={(event) => { invalidate(); setEnd(event.target.value); }} />
        <Button variant="outlined" disabled={loading} onClick={() => read(1)}>{loading ? 'Consultando…' : 'Consultar relatório 99Food'}</Button>
      </Stack>
      <Typography variant="caption" color="text.secondary">Períodos de até 31 dias, dentro dos últimos três meses.</Typography>
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      {visibleResult && !visibleResult.records.length && <Alert severity="info" sx={{ mt: 2 }}>A 99Food não retornou registros para este período.</Alert>}
      {visibleResult?.records.length > 0 && <>
        <TableContainer sx={{ mt: 2 }}><Table size="small" aria-label="Relatório financeiro da 99Food">
          <TableHead><TableRow>{columns.map(([key, title]) => <TableCell key={key}>{title}</TableCell>)}</TableRow></TableHead>
          <TableBody>{visibleResult.records.map((record, index) => <TableRow key={index}>{columns.map(([key, _label, money]) => <TableCell key={key} sx={{ whiteSpace: 'nowrap' }}>{money ? formatNinetyNineFoodCents(record[key]) : record[key] === undefined || record[key] === null ? '—' : String(record[key])}</TableCell>)}</TableRow>)}</TableBody>
        </Table></TableContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
          <Button disabled={loading || Number(visibleResult.page || 1) <= 1} onClick={() => read(Number(visibleResult.page) - 1)}>Anterior</Button>
          <Typography variant="body2">Página {visibleResult.page || 1} de {visibleResult.totalPages || 1} · {visibleResult.totalRecords ?? visibleResult.records.length} registros</Typography>
          <Button disabled={loading || Number(visibleResult.page || 1) >= Number(visibleResult.totalPages || 1)} onClick={() => read(Number(visibleResult.page || 1) + 1)}>Próxima</Button>
        </Box>
      </>}
    </CardContent></Card>
  );
};
export default NinetyNineFoodFinancialPanel;
